import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { invoicesAPI, adminUsersAPI, prospectsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
    FileText, Plus, Send, CheckCircle, Clock, Banknote,
    Zap, HardHat, Filter, Eye, Loader2, AlertCircle,
    Building2, User, Users, Download, RotateCcw, ArrowDownLeft,
    Search, X
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_CONFIG = {
    brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: Clock },
    emise: { label: 'Émise', color: 'bg-blue-100 text-blue-700', icon: Send },
    payee: { label: 'Payée', color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

const TYPE_CONFIG = {
    facture: { label: 'Facture', color: 'text-slate-700', icon: FileText },
    avoir: { label: 'Avoir', color: 'text-red-600', icon: ArrowDownLeft }
};

const ACTOR_CONFIG = {
    valnexo: { label: 'Valnexo', icon: Building2, color: 'text-red-600' },
    vp: { label: 'VP', icon: Users, color: 'text-purple-600' },
    apporteur: { label: 'Apporteur', icon: User, color: 'text-green-600' }
};

const PIPELINE_CONFIG = {
    easy_power: { label: 'Easy Power', icon: Zap, color: 'text-amber-600' },
    chantier: { label: 'Chantier', icon: HardHat, color: 'text-blue-600' }
};

export default function Invoices() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [pendingCommissions, setPendingCommissions] = useState([]);
    const [activeTab, setActiveTab] = useState('invoices');
    
    // Create invoice dialog
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [selectedCommissions, setSelectedCommissions] = useState([]);
    const [newInvoice, setNewInvoice] = useState({
        actor_type: '',
        actor_id: '',
        notes: ''
    });
    const [creating, setCreating] = useState(false);
    
    // View invoice dialog
    const [showViewDialog, setShowViewDialog] = useState(false);
    const [viewingInvoice, setViewingInvoice] = useState(null);
    
    // Credit note dialog
    const [showCreditNoteDialog, setShowCreditNoteDialog] = useState(false);
    const [creditNoteInvoice, setCreditNoteInvoice] = useState(null);
    const [creditNoteReason, setCreditNoteReason] = useState('');
    const [creatingCreditNote, setCreatingCreditNote] = useState(false);
    
    // Filters
    const [statusFilter, setStatusFilter] = useState('all');
    const [pipelineFilter, setPipelineFilter] = useState('all');
    const [actorFilter, setActorFilter] = useState('all');

    // Search state (Full Admin only)
    const [searchQuery, setSearchQuery] = useState('');
    const [searchUserId, setSearchUserId] = useState('all');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [searchTotal, setSearchTotal] = useState(0);

    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = (user?.role === 'admin' || user?.is_admin === true);
    const isComptable = user?.role === 'comptable';
    const canCreateInvoice = isAdminFull || isComptable;
    const canSearchAll = isAdminFull || isAdmin || isComptable;

    useEffect(() => {
        loadData();
        if (canSearchAll) {
            loadUsers();
        }
    }, [canSearchAll]);

    const loadUsers = async () => {
        try {
            const res = await adminUsersAPI.getAll();
            setUsers(res.data || []);
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const loadData = async () => {
        try {
            const [invoicesRes, pendingRes] = await Promise.all([
                invoicesAPI.getAll(),
                invoicesAPI.getPendingCommissions()
            ]);
            setInvoices(invoicesRes.data || []);
            setPendingCommissions(pendingRes.data || []);
        } catch (error) {
            console.error('Error loading invoices:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    // Recherche de factures (Full Admin)
    const handleSearch = async () => {
        if (!canSearchAll) return;
        
        setSearchLoading(true);
        try {
            const params = {};
            if (searchQuery.trim()) {
                params.prospect_name = searchQuery.trim();
            }
            if (searchUserId && searchUserId !== 'all') {
                params.user_id = searchUserId;
            }
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            if (pipelineFilter !== 'all') {
                params.pipeline_type = pipelineFilter;
            }

            const res = await invoicesAPI.search(params);
            setSearchResults(res.data?.invoices || []);
            setSearchTotal(res.data?.total || 0);
        } catch (error) {
            console.error('Search error:', error);
            toast.error('Erreur lors de la recherche');
        } finally {
            setSearchLoading(false);
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchUserId('all');
        setSearchResults([]);
        setSearchTotal(0);
    };

    const handleCreateInvoice = async () => {
        if (selectedCommissions.length === 0) {
            toast.error('Sélectionnez au moins une commission');
            return;
        }
        if (!newInvoice.actor_type) {
            toast.error("Sélectionnez un type d'acteur");
            return;
        }

        setCreating(true);
        try {
            await invoicesAPI.create({
                actor_type: newInvoice.actor_type,
                actor_id: newInvoice.actor_id || null,
                commission_ids: selectedCommissions,
                notes: newInvoice.notes || null
            });
            toast.success('Facture créée avec succès');
            setShowCreateDialog(false);
            setSelectedCommissions([]);
            setNewInvoice({ actor_type: '', actor_id: '', notes: '' });
            await loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setCreating(false);
        }
    };

    const handleIssueInvoice = async (invoiceId) => {
        try {
            await invoicesAPI.issue(invoiceId);
            toast.success('Facture émise avec succès');
            await loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de l'émission");
        }
    };

    const handleMarkPaid = async (invoiceId) => {
        try {
            await invoicesAPI.markPaid(invoiceId);
            toast.success('Facture marquée comme payée');
            await loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du marquage');
        }
    };

    const handleCreateCreditNote = async () => {
        if (!creditNoteReason.trim()) {
            toast.error('Veuillez indiquer le motif de l\'avoir');
            return;
        }

        setCreatingCreditNote(true);
        try {
            await invoicesAPI.createCreditNote({
                original_invoice_id: creditNoteInvoice.id,
                line_items: [],
                reason: creditNoteReason,
                partial: false
            });
            toast.success('Avoir créé avec succès');
            setShowCreditNoteDialog(false);
            setCreditNoteInvoice(null);
            setCreditNoteReason('');
            await loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'avoir');
        } finally {
            setCreatingCreditNote(false);
        }
    };

    const openCreditNoteDialog = (invoice) => {
        setCreditNoteInvoice(invoice);
        setCreditNoteReason('');
        setShowCreditNoteDialog(true);
    };

    const handleDownloadPdf = async (invoice) => {
        try {
            toast.info('Génération du PDF en cours...');
            const response = await invoicesAPI.downloadPdf(invoice.id);
            
            // Créer un blob avec le type MIME correct
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const filename = `${invoice.invoice_number}.pdf`;
            
            // Méthode universelle pour PC, Mac, Mobile et Tablette
            // Vérifier si le navigateur supporte navigator.msSaveOrOpenBlob (IE/Edge legacy)
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(blob, filename);
            } else {
                // Créer une URL blob
                const blobUrl = window.URL.createObjectURL(blob);
                
                // Créer un lien temporaire
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                
                // Pour iOS Safari, il faut ouvrir dans un nouvel onglet
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                
                if (isIOS && isSafari) {
                    // Sur iOS Safari, ouvrir le PDF dans un nouvel onglet
                    // L'utilisateur pourra ensuite utiliser le bouton de partage pour sauvegarder
                    window.open(blobUrl, '_blank');
                    toast.success('PDF ouvert - Utilisez le bouton de partage pour sauvegarder');
                } else {
                    // Méthode standard pour tous les autres navigateurs
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    
                    // Nettoyer après un court délai
                    setTimeout(() => {
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                    }, 100);
                    
                    toast.success('PDF téléchargé dans votre dossier Téléchargements');
                }
            }
        } catch (error) {
            console.error('PDF download error:', error);
            toast.error('Erreur lors du téléchargement du PDF');
        }
    };

    const toggleCommissionSelection = (commissionId) => {
        setSelectedCommissions(prev => 
            prev.includes(commissionId)
                ? prev.filter(id => id !== commissionId)
                : [...prev, commissionId]
        );
    };

    const selectAllCommissions = () => {
        if (selectedCommissions.length === pendingCommissions.length) {
            setSelectedCommissions([]);
        } else {
            setSelectedCommissions(pendingCommissions.map(c => c.commission_id));
        }
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '-';
        return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
    };

    // Filter invoices
    const filteredInvoices = invoices.filter(inv => {
        if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
        if (actorFilter !== 'all' && inv.actor_type !== actorFilter) return false;
        if (pipelineFilter !== 'all') {
            const hasPipeline = inv.line_items?.some(li => li.pipeline_type === pipelineFilter);
            if (!hasPipeline) return false;
        }
        return true;
    });

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-96 w-full" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-outfit font-semibold text-slate-900">
                            Facturation
                        </h1>
                        <p className="text-slate-600 mt-1 text-sm sm:text-base">
                            Gestion des factures basées sur les commissions validées
                        </p>
                    </div>
                    {canCreateInvoice && (
                        <Button
                            onClick={() => setShowCreateDialog(true)}
                            className="bg-slate-900 hover:bg-slate-800"
                            data-testid="create-invoice-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvelle Facture
                        </Button>
                    )}
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">En attente</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {pendingCommissions.length}
                                    </p>
                                </div>
                                <Clock className="w-8 h-8 text-amber-500" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Brouillons</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {invoices.filter(i => i.status === 'brouillon').length}
                                    </p>
                                </div>
                                <FileText className="w-8 h-8 text-slate-500" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Émises</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {invoices.filter(i => i.status === 'emise').length}
                                    </p>
                                </div>
                                <Send className="w-8 h-8 text-blue-500" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Payées</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {invoices.filter(i => i.status === 'payee').length}
                                    </p>
                                </div>
                                <CheckCircle className="w-8 h-8 text-green-500" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-red-200 bg-red-50/30">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Avoirs</p>
                                    <p className="text-2xl font-bold text-red-600">
                                        {invoices.filter(i => i.invoice_type === 'avoir').length}
                                    </p>
                                </div>
                                <ArrowDownLeft className="w-8 h-8 text-red-500" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="invoices" data-testid="tab-invoices">
                            <FileText className="w-4 h-4 mr-2" />
                            Factures ({invoices.length})
                        </TabsTrigger>
                        <TabsTrigger value="pending" data-testid="tab-pending">
                            <Clock className="w-4 h-4 mr-2" />
                            En attente ({pendingCommissions.length})
                        </TabsTrigger>
                        {canSearchAll && (
                            <TabsTrigger value="search" data-testid="tab-search">
                                <Search className="w-4 h-4 mr-2" />
                                Recherche
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {/* Invoices Tab */}
                    <TabsContent value="invoices" className="mt-6">
                        {/* Filters */}
                        <div className="flex flex-wrap gap-3 mb-4">
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-40">
                                    <Filter className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder="Statut" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les statuts</SelectItem>
                                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={actorFilter} onValueChange={setActorFilter}>
                                <SelectTrigger className="w-40">
                                    <SelectValue placeholder="Acteur" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les acteurs</SelectItem>
                                    {Object.entries(ACTOR_CONFIG).map(([key, config]) => (
                                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
                                <SelectTrigger className="w-40">
                                    <SelectValue placeholder="Pipeline" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les pipelines</SelectItem>
                                    {Object.entries(PIPELINE_CONFIG).map(([key, config]) => (
                                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Invoices Table */}
                        <Card className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead>N° Document</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Acteur</TableHead>
                                        <TableHead>Lignes</TableHead>
                                        <TableHead>Total HT</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredInvoices.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                                                <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                                                <p>Aucune facture trouvée</p>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredInvoices.map((invoice) => {
                                            const actorConfig = ACTOR_CONFIG[invoice.actor_type];
                                            const statusConfig = STATUS_CONFIG[invoice.status];
                                            const typeConfig = TYPE_CONFIG[invoice.invoice_type] || TYPE_CONFIG.facture;
                                            const StatusIcon = statusConfig?.icon || Clock;
                                            const ActorIcon = actorConfig?.icon || Building2;
                                            const TypeIcon = typeConfig?.icon || FileText;
                                            const isAvoir = invoice.invoice_type === 'avoir';
                                            const canCreateAvoir = !isAvoir && 
                                                (invoice.status === 'emise' || invoice.status === 'payee') &&
                                                canCreateInvoice;
                                            
                                            return (
                                                <TableRow 
                                                    key={invoice.id} 
                                                    data-testid={`invoice-row-${invoice.id}`}
                                                    className={isAvoir ? 'bg-red-50/30' : ''}
                                                >
                                                    <TableCell className="font-mono font-medium">
                                                        <div>
                                                            {invoice.invoice_number}
                                                            {isAvoir && invoice.original_invoice_number && (
                                                                <p className="text-xs text-slate-500">
                                                                    Réf: {invoice.original_invoice_number}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge 
                                                            variant={isAvoir ? "destructive" : "outline"} 
                                                            className={isAvoir ? '' : 'bg-slate-50'}
                                                        >
                                                            <TypeIcon className="w-3 h-3 mr-1" />
                                                            {typeConfig?.label}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <ActorIcon className={`w-4 h-4 ${actorConfig?.color}`} />
                                                            <div>
                                                                <p className="font-medium">{invoice.actor_name}</p>
                                                                <p className="text-xs text-slate-500">{actorConfig?.label}</p>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {invoice.line_items?.length || 0} commission(s)
                                                    </TableCell>
                                                    <TableCell className={`font-bold ${isAvoir ? 'text-red-600' : ''}`}>
                                                        {formatCurrency(invoice.total_ht)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={statusConfig?.color}>
                                                            <StatusIcon className="w-3 h-3 mr-1" />
                                                            {statusConfig?.label}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-sm text-slate-600">
                                                        {format(new Date(invoice.created_at), 'dd/MM/yyyy', { locale: fr })}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center justify-end gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setViewingInvoice(invoice);
                                                                    setShowViewDialog(true);
                                                                }}
                                                                title="Voir détails"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleDownloadPdf(invoice)}
                                                                title="Télécharger PDF"
                                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                data-testid={`download-pdf-${invoice.id}`}
                                                            >
                                                                <Download className="w-4 h-4" />
                                                            </Button>
                                                            {invoice.status === 'brouillon' && canCreateInvoice && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleIssueInvoice(invoice.id)}
                                                                    className="text-blue-600"
                                                                >
                                                                    <Send className="w-4 h-4 mr-1" />
                                                                    Émettre
                                                                </Button>
                                                            )}
                                                            {invoice.status === 'emise' && !isAvoir && canCreateInvoice && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleMarkPaid(invoice.id)}
                                                                    className="text-green-600"
                                                                >
                                                                    <CheckCircle className="w-4 h-4 mr-1" />
                                                                    Payée
                                                                </Button>
                                                            )}
                                                            {canCreateAvoir && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => openCreditNoteDialog(invoice)}
                                                                    className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                                                                    title="Créer un avoir"
                                                                    data-testid={`create-credit-note-${invoice.id}`}
                                                                >
                                                                    <RotateCcw className="w-4 h-4 mr-1" />
                                                                    Avoir
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </Card>
                    </TabsContent>

                    {/* Pending Commissions Tab */}
                    <TabsContent value="pending" className="mt-6">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle>Commissions validées en attente de facturation</CardTitle>
                                        <CardDescription>
                                            Sélectionnez les commissions à inclure dans une facture
                                        </CardDescription>
                                    </div>
                                    {pendingCommissions.length > 0 && canCreateInvoice && (
                                        <Button
                                            onClick={() => setShowCreateDialog(true)}
                                            disabled={selectedCommissions.length === 0}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Créer facture ({selectedCommissions.length})
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent>
                                {pendingCommissions.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
                                        <p>Toutes les commissions ont été facturées</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-slate-50">
                                                <TableHead className="w-12">
                                                    <Checkbox
                                                        checked={selectedCommissions.length === pendingCommissions.length}
                                                        onCheckedChange={selectAllCommissions}
                                                    />
                                                </TableHead>
                                                <TableHead>Prospect</TableHead>
                                                <TableHead>Pipeline</TableHead>
                                                <TableHead>Acteur</TableHead>
                                                <TableHead>Montant</TableHead>
                                                <TableHead>Validée le</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {pendingCommissions.map((commission) => {
                                                const pipelineConfig = PIPELINE_CONFIG[commission.pipeline_type];
                                                const PipelineIcon = pipelineConfig?.icon || Zap;
                                                const actorConfig = ACTOR_CONFIG[commission.actor_type];
                                                const ActorIcon = actorConfig?.icon || Building2;
                                                
                                                return (
                                                    <TableRow key={commission.commission_id}>
                                                        <TableCell>
                                                            <Checkbox
                                                                checked={selectedCommissions.includes(commission.commission_id)}
                                                                onCheckedChange={() => toggleCommissionSelection(commission.commission_id)}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="font-medium">
                                                            {commission.prospect_name}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline" className={pipelineConfig?.color}>
                                                                <PipelineIcon className="w-3 h-3 mr-1" />
                                                                {pipelineConfig?.label}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <ActorIcon className={`w-4 h-4 ${actorConfig?.color}`} />
                                                                <div>
                                                                    <p className="font-medium text-sm">{commission.actor_name}</p>
                                                                    <p className="text-xs text-slate-500">{actorConfig?.label}</p>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="font-bold text-green-600">
                                                            {formatCurrency(commission.amount)}
                                                        </TableCell>
                                                        <TableCell className="text-sm text-slate-600">
                                                            {commission.validated_at 
                                                                ? format(new Date(commission.validated_at), 'dd/MM/yyyy', { locale: fr })
                                                                : '-'
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Search Tab - Full Admin Only */}
                    {canSearchAll && (
                        <TabsContent value="search" className="mt-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Search className="w-5 h-5" />
                                        Recherche de Factures
                                    </CardTitle>
                                    <CardDescription>
                                        Recherchez par utilisateur ou par client (dossier) - Easy Power et Chantier
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {/* Search Filters */}
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                        <div className="space-y-2">
                                            <Label>Recherche par client</Label>
                                            <Input
                                                placeholder="Nom du client..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                                data-testid="search-client-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Utilisateur</Label>
                                            <Select value={searchUserId} onValueChange={setSearchUserId}>
                                                <SelectTrigger data-testid="search-user-select">
                                                    <SelectValue placeholder="Tous les utilisateurs" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Tous les utilisateurs</SelectItem>
                                                    {users.filter(u => u.role === 'vp' || u.role === 'apporteur').map((u) => (
                                                        <SelectItem key={u.id} value={u.id}>
                                                            {u.first_name} {u.last_name} ({u.role === 'vp' ? 'VP' : 'Apporteur'})
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Pipeline</Label>
                                            <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Tous" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Tous</SelectItem>
                                                    <SelectItem value="easy_power">Easy Power</SelectItem>
                                                    <SelectItem value="chantier">Chantier</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="flex items-end gap-2">
                                            <Button 
                                                onClick={handleSearch} 
                                                disabled={searchLoading}
                                                className="flex-1"
                                                data-testid="search-btn"
                                            >
                                                {searchLoading ? (
                                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                ) : (
                                                    <Search className="w-4 h-4 mr-2" />
                                                )}
                                                Rechercher
                                            </Button>
                                            {(searchQuery || searchUserId !== 'all') && (
                                                <Button variant="ghost" size="icon" onClick={clearSearch}>
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Search Results */}
                                    {searchResults.length > 0 ? (
                                        <>
                                            <div className="mb-4 text-sm text-slate-500">
                                                {searchTotal} facture(s) trouvée(s)
                                            </div>
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="bg-slate-50">
                                                        <TableHead>N° Facture</TableHead>
                                                        <TableHead>Client Facturé</TableHead>
                                                        <TableHead>Pipeline</TableHead>
                                                        <TableHead>Émetteur</TableHead>
                                                        <TableHead>Montant HT</TableHead>
                                                        <TableHead>Statut</TableHead>
                                                        <TableHead>Date</TableHead>
                                                        <TableHead className="text-right">Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {searchResults.map((invoice) => {
                                                        const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.brouillon;
                                                        const StatusIcon = statusConfig?.icon || Clock;
                                                        const pipelineConfig = PIPELINE_CONFIG[invoice.pipeline_type];
                                                        const PipelineIcon = pipelineConfig?.icon || Zap;
                                                        
                                                        return (
                                                            <TableRow key={invoice.id} data-testid={`search-result-${invoice.id}`}>
                                                                <TableCell className="font-mono font-medium">
                                                                    {invoice.invoice_number}
                                                                </TableCell>
                                                                <TableCell className="font-medium">
                                                                    {invoice.prospect_name || 'N/A'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {pipelineConfig && (
                                                                        <Badge variant="outline" className={pipelineConfig.color}>
                                                                            <PipelineIcon className="w-3 h-3 mr-1" />
                                                                            {pipelineConfig.label}
                                                                        </Badge>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {invoice.issuer_name || invoice.actor_name || 'N/A'}
                                                                </TableCell>
                                                                <TableCell className="font-bold">
                                                                    {invoice.total_ht?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Badge className={statusConfig?.color}>
                                                                        <StatusIcon className="w-3 h-3 mr-1" />
                                                                        {statusConfig?.label}
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell className="text-sm text-slate-600">
                                                                    {invoice.created_at ? format(new Date(invoice.created_at), 'dd/MM/yyyy', { locale: fr }) : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                setViewingInvoice(invoice);
                                                                                setShowViewDialog(true);
                                                                            }}
                                                                            title="Voir détails"
                                                                        >
                                                                            <Eye className="w-4 h-4" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleDownloadPdf(invoice)}
                                                                            title="Télécharger PDF"
                                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                        >
                                                                            <Download className="w-4 h-4" />
                                                                        </Button>
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </>
                                    ) : searchLoading ? (
                                        <div className="text-center py-12">
                                            <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" />
                                            <p className="mt-2 text-slate-500">Recherche en cours...</p>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 text-slate-500">
                                            <Search className="w-10 h-10 mx-auto mb-3 opacity-50" />
                                            <p>Utilisez les filtres ci-dessus pour rechercher des factures</p>
                                            <p className="text-sm mt-1">Par utilisateur, par client (dossier), Easy Power ou Chantier</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                </Tabs>
            </div>

            {/* Create Invoice Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Créer une facture</DialogTitle>
                        <DialogDescription>
                            {selectedCommissions.length} commission(s) sélectionnée(s)
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Type d&apos;acteur *</Label>
                            <Select 
                                value={newInvoice.actor_type} 
                                onValueChange={(v) => setNewInvoice(prev => ({ ...prev, actor_type: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Sélectionnez un type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(ACTOR_CONFIG).map(([key, config]) => {
                                        const Icon = config.icon;
                                        return (
                                            <SelectItem key={key} value={key}>
                                                <div className="flex items-center gap-2">
                                                    <Icon className={`w-4 h-4 ${config.color}`} />
                                                    {config.label}
                                                </div>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Notes (optionnel)</Label>
                            <Textarea
                                value={newInvoice.notes}
                                onChange={(e) => setNewInvoice(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Notes pour cette facture..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleCreateInvoice} disabled={creating}>
                            {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Créer la facture
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View Invoice Dialog */}
            <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {viewingInvoice?.invoice_type === 'avoir' ? (
                                <ArrowDownLeft className="w-5 h-5 text-red-600" />
                            ) : (
                                <FileText className="w-5 h-5" />
                            )}
                            {viewingInvoice?.invoice_type === 'avoir' ? 'Avoir' : 'Facture'} {viewingInvoice?.invoice_number}
                        </DialogTitle>
                    </DialogHeader>
                    {viewingInvoice && (
                        <div className="space-y-6">
                            {/* Invoice Header */}
                            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                                <div>
                                    <p className="text-sm text-slate-500">Acteur</p>
                                    <p className="font-medium">{viewingInvoice.actor_name}</p>
                                    <p className="text-sm text-slate-500">{ACTOR_CONFIG[viewingInvoice.actor_type]?.label}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Statut</p>
                                    <Badge className={STATUS_CONFIG[viewingInvoice.status]?.color}>
                                        {STATUS_CONFIG[viewingInvoice.status]?.label}
                                    </Badge>
                                </div>
                            </div>
                            
                            {/* Credit Note Reference */}
                            {viewingInvoice.invoice_type === 'avoir' && viewingInvoice.original_invoice_number && (
                                <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                                    <p className="text-sm text-red-800">
                                        <strong>Facture d&apos;origine:</strong> {viewingInvoice.original_invoice_number}
                                    </p>
                                    {viewingInvoice.reason && (
                                        <p className="text-sm text-red-700 mt-1">
                                            <strong>Motif:</strong> {viewingInvoice.reason}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Line Items */}
                            <div>
                                <h4 className="font-medium mb-2">Lignes de facture</h4>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Prospect</TableHead>
                                            <TableHead>Pipeline</TableHead>
                                            <TableHead className="text-right">Montant</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {viewingInvoice.line_items?.map((item, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{item.prospect_name}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">
                                                        {PIPELINE_CONFIG[item.pipeline_type]?.label}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right font-medium">
                                                    {formatCurrency(item.commission_amount)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Totals */}
                            <div className="border-t pt-4 space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-slate-600">Total HT</span>
                                    <span className={`font-medium ${viewingInvoice.invoice_type === 'avoir' ? 'text-red-600' : ''}`}>
                                        {formatCurrency(viewingInvoice.total_ht)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-600">TVA ({viewingInvoice.tva_rate}%)</span>
                                    <span className={`font-medium ${viewingInvoice.invoice_type === 'avoir' ? 'text-red-600' : ''}`}>
                                        {formatCurrency(viewingInvoice.tva_amount)}
                                    </span>
                                </div>
                                <div className={`flex justify-between text-lg font-bold border-t pt-2 ${viewingInvoice.invoice_type === 'avoir' ? 'text-red-600' : ''}`}>
                                    <span>Total TTC</span>
                                    <span>{formatCurrency(viewingInvoice.total_ttc)}</span>
                                </div>
                            </div>

                            {/* Dates */}
                            <div className="text-sm text-slate-500 border-t pt-4">
                                <p>Créée le {format(new Date(viewingInvoice.created_at), 'dd MMMM yyyy', { locale: fr })}</p>
                                {viewingInvoice.issued_at && (
                                    <p>Émise le {format(new Date(viewingInvoice.issued_at), 'dd MMMM yyyy', { locale: fr })}</p>
                                )}
                                {viewingInvoice.paid_at && (
                                    <p>Payée le {format(new Date(viewingInvoice.paid_at), 'dd MMMM yyyy', { locale: fr })}</p>
                                )}
                            </div>

                            {/* Download PDF Button */}
                            <div className="border-t pt-4">
                                <Button
                                    onClick={() => handleDownloadPdf(viewingInvoice)}
                                    className="w-full bg-red-600 hover:bg-red-700"
                                    data-testid="download-pdf-dialog"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Télécharger en PDF
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Credit Note Dialog */}
            <Dialog open={showCreditNoteDialog} onOpenChange={setShowCreditNoteDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <RotateCcw className="w-5 h-5" />
                            Créer un avoir
                        </DialogTitle>
                        <DialogDescription>
                            Créer un avoir pour annuler la facture {creditNoteInvoice?.invoice_number}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {creditNoteInvoice && (
                        <div className="space-y-4 py-4">
                            {/* Invoice Info */}
                            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Facture:</span>
                                    <span className="font-mono font-medium">{creditNoteInvoice.invoice_number}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Acteur:</span>
                                    <span className="font-medium">{creditNoteInvoice.actor_name}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Montant HT:</span>
                                    <span className="font-bold">{formatCurrency(creditNoteInvoice.total_ht)}</span>
                                </div>
                            </div>

                            {/* Warning */}
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                                <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                                <div className="text-sm text-amber-800">
                                    <p className="font-medium">Attention</p>
                                    <p>L&apos;émission de cet avoir annulera la totalité de la facture et remettra les commissions au statut validée.</p>
                                </div>
                            </div>

                            {/* Reason Input */}
                            <div className="space-y-2">
                                <Label>Motif de l&apos;avoir *</Label>
                                <Textarea
                                    value={creditNoteReason}
                                    onChange={(e) => setCreditNoteReason(e.target.value)}
                                    placeholder="Indiquez le motif de l&apos;avoir (erreur de facturation, annulation de commande, etc.)"
                                    rows={3}
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setShowCreditNoteDialog(false)}
                            disabled={creatingCreditNote}
                        >
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleCreateCreditNote} 
                            disabled={creatingCreditNote || !creditNoteReason.trim()}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {creatingCreditNote && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Créer l&apos;avoir
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
